import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.css";
import backgroundImage from "../images/AndarBahar/tableBg.jpg";
import welcome from "../images/AndarBahar/welcome.gif";
import ideal from "../images/AndarBahar/idle.gif";
import clapping from "../images/AndarBahar/clapping.gif";
import crown from "../images/AndarBahar/crown.png";
import closedCard from "../images/AndarBahar/closed-card.jpg";
import { cards } from "../images/index";
import useGameScreenLoader from "../../../api/useGameScreenLoader";
import GameAreaLoader from "../../Loader/GameAreaLoader";

export default function GameScreen({
  bettingClosed,
  socketData,
  remainingTime,
  onAnimStart,
  onAnimEnd,
}) {
  const [jokerCard, setJokerCard] = useState(null);
  const [jokerRank, setJokerRank] = useState(null);
  const [revealedAndar, setRevealedAndar] = useState([]);
  const [revealedBahar, setRevealedBahar] = useState([]);
  const [winner, setWinner] = useState(null);
  const [showClapping, setShowClapping] = useState(false);
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const remainingTimeZeroFiredRef = useRef(false);
  const prevRoundIdRef = useRef(null);
  const prevBettingClosedRef = useRef(false);
  const jokerRankRef = useRef(null);
  const processedEventsRef = useRef(new Set());
  const initialRoundIdRef = useRef(null);
  const jokerRevealedRef = useRef(false);
  const animEndFiredRef = useRef(false);
  const settlementProcessedRef = useRef(new Set());
  const revealQueueRef = useRef([]);
  const isRevealingRef = useRef(false);
  const isFirstRevealOfRoundRef = useRef(true);

  const REVEAL_GAP_MS = 1000;
  const JOKER_TO_FIRST_CARD_GAP_MS = 1000;

  const roundId = socketData?.roundId != null ? String(socketData.roundId) : null;
  const settlement = socketData?.settlement;

  if (initialRoundIdRef.current === null && roundId) {
    initialRoundIdRef.current = roundId;
  }

  const { gameScreenLoader, processingLoader } = useGameScreenLoader(roundId, !!settlement, 5000);

  // ====================== Helpers ======================
  const toCardArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === "string" && val.length) return val.split(",").map((s) => s.trim());
    return [];
  };

  const extractRank = (cardString) => {
    if (!cardString || typeof cardString !== "string") return null;
    const raw = cardString.trim().toUpperCase().replace(/\.(WEBP|PNG|JPE?G|SVG)$/i, "");
    const normalized = raw.replace(/\bOF\b/g, "").trim();
    const parts = normalized.split(/\s+/);
    let rankRaw = parts[0];

    if (parts.length === 1) {
      const m = raw.match(/^(10|[A2-9JQK])/);
      rankRaw = m ? m[1] : raw[0];
    }

    if (rankRaw === "ACE" || rankRaw === "1") return "A";
    if (rankRaw === "KING") return "K";
    if (rankRaw === "QUEEN") return "Q";
    if (rankRaw === "JACK") return "J";
    return rankRaw;
  };

  const mapCardStringToAsset = (cardString) => {
    if (!cardString || typeof cardString !== "string") return null;
    const raw = cardString.trim().toUpperCase().replace(/\.(WEBP|PNG|JPE?G|SVG)$/i, "");

    if (cards[raw]) return cards[raw];
    if (cards[`${raw}.webp`]) return cards[`${raw}.webp`];

    const normalized = raw.replace(/\bOF\b/g, "").trim();
    const parts = normalized.split(/\s+/);
    let rankRaw, suitRaw;

    if (parts.length >= 2) {
      rankRaw = parts[0];
      suitRaw = parts[parts.length - 1];
    } else {
      const m = raw.match(/^(10|[A2-9JQK])\s*(SPADE\w*|HEART\w*|DIAM\w*|CLUB\w*|S|H|D|C)?$/);
      if (!m) return null;
      rankRaw = m[1];
      suitRaw = m[2] || "";
    }

    const suitCode = suitRaw.startsWith("S") ? "SS"
                  : suitRaw.startsWith("H") ? "HH"
                  : suitRaw.startsWith("D") ? "DD"
                  : suitRaw.startsWith("C") ? "CC" : null;

    if (!suitCode) return null;

    const rank = rankRaw === "ACE" ? "A"
               : rankRaw === "KING" ? "K"
               : rankRaw === "QUEEN" ? "Q"
               : rankRaw === "JACK" ? "J"
               : rankRaw === "1" ? "A" : rankRaw;

    const key = `${rank}${suitCode}`;
    return cards[key] || cards[`${key}.webp`] || null;
  };

  const waitForJoker = () =>
    new Promise((resolve) => {
      if (jokerRevealedRef.current) return resolve();
      const interval = setInterval(() => {
        if (jokerRevealedRef.current) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, 5000);
    });

  const processRevealQueue = (isFirstCall = false) => {
    if (isRevealingRef.current) return;
    if (revealQueueRef.current.length === 0) return;

    isRevealingRef.current = true;
    const next = revealQueueRef.current.shift();
    const gap = isFirstCall ? JOKER_TO_FIRST_CARD_GAP_MS : REVEAL_GAP_MS;

    setTimeout(() => {
      if (next.side === "ANDAR") {
        setRevealedAndar((prev) => {
          if (prev.includes(next.asset) && prev[prev.length - 1] === next.asset) return prev;
          return [...prev, next.asset];
        });
      } else if (next.side === "BAHAR") {
        setRevealedBahar((prev) => {
          if (prev.includes(next.asset) && prev[prev.length - 1] === next.asset) return prev;
          return [...prev, next.asset];
        });
      }
      isRevealingRef.current = false;
      processRevealQueue(false);
    }, gap);
  };

  // ====================== Track first data arrival (for loader) ======================
  useEffect(() => {
    if (socketData && !hasReceivedData) {
      setHasReceivedData(true);
    }
  }, [socketData, hasReceivedData]);

  // ====================== Round Change (reset only, no cache) ======================
  useEffect(() => {
    if (!socketData?.roundId) return;
    const currentRoundId = String(socketData.roundId);

    if (currentRoundId !== prevRoundIdRef.current) {
      prevRoundIdRef.current = currentRoundId;

      const jc = mapCardStringToAsset(socketData.jokerCard || socketData.joker);
      const jr = extractRank(socketData.jokerCard || socketData.joker);
      const ra = toCardArray(socketData.andarCards || socketData.revealedAndar)
        .map(mapCardStringToAsset).filter(Boolean);
      const rb = toCardArray(socketData.baharCards || socketData.revealedBahar)
        .map(mapCardStringToAsset).filter(Boolean);

      if (jc || ra.length || rb.length) {
        setJokerCard(jc);
        setJokerRank(jr);
        jokerRankRef.current = jr;
        jokerRevealedRef.current = !!jc;
        setRevealedAndar(ra);
        setRevealedBahar(rb);
      } else {
        setJokerCard(null);
        setJokerRank(null);
        jokerRankRef.current = null;
        jokerRevealedRef.current = false;
        setRevealedAndar([]);
        setRevealedBahar([]);
      }

      setWinner(null);
      setShowClapping(false);
      prevBettingClosedRef.current = false;
      processedEventsRef.current.clear();
      animEndFiredRef.current = false;
      remainingTimeZeroFiredRef.current = false;
      revealQueueRef.current = [];
      isRevealingRef.current = false;
      isFirstRevealOfRoundRef.current = true;
      onAnimEnd?.();
    }
  }, [socketData?.roundId, onAnimEnd]);

  // ====================== ROUND_STATE (reconnect / remount) ======================
  useEffect(() => {
    if (!socketData || socketData.type !== "ROUND_STATE") return;
    const rId = String(socketData.roundId);
    if (!rId) return;

    const jc = mapCardStringToAsset(socketData.jokerCard || socketData.joker);
    const jr = extractRank(socketData.jokerCard || socketData.joker);
    const ra = toCardArray(socketData.andarCards || socketData.revealedAndar)
      .map(mapCardStringToAsset).filter(Boolean);
    const rb = toCardArray(socketData.baharCards || socketData.revealedBahar)
      .map(mapCardStringToAsset).filter(Boolean);

    const w = socketData.winner
      ? (String(socketData.winner).toUpperCase().includes("ANDAR") ? "ANDAR" : "BAHAR")
      : null;

    if (socketData.remainingTime > 0 && !w) {
      setJokerCard(null);
      setJokerRank(null);
      jokerRankRef.current = null;
      jokerRevealedRef.current = false;
      setRevealedAndar([]);
      setRevealedBahar([]);
      setWinner(null);
      setShowClapping(false);
      onAnimEnd?.();
      return;
    }

    setJokerCard(jc);
    setJokerRank(jr);
    jokerRankRef.current = jr;
    jokerRevealedRef.current = !!jc;

    // Length-based diff only — never duplicates, never shrinks
    setRevealedAndar((prev) => (ra.length > prev.length ? ra : prev));
    setRevealedBahar((prev) => (rb.length > prev.length ? rb : prev));

    setWinner(w);
    setShowClapping(!!w);

    if (!w) onAnimEnd?.();
  }, [socketData]);

  // ====================== Betting Closed ======================
  useEffect(() => {
    if (bettingClosed && !prevBettingClosedRef.current) {
      prevBettingClosedRef.current = true;
      animEndFiredRef.current = false;
      onAnimStart?.();
    }
  }, [bettingClosed, onAnimStart]);

  // ====================== remainingTime hits 0 -> reveal joker after 3s ======================
  useEffect(() => {
    if (remainingTime === 0 && !remainingTimeZeroFiredRef.current && !jokerCard) {
      remainingTimeZeroFiredRef.current = true;

      setTimeout(() => {
        if (jokerRevealedRef.current) return;

        if (socketData?.jokerCard || socketData?.joker) {
          const asset = mapCardStringToAsset(socketData.jokerCard || socketData.joker);
          const rank = extractRank(socketData.jokerCard || socketData.joker);
          if (asset) {
            setJokerCard(asset);
            setJokerRank(rank);
            jokerRankRef.current = rank;
            jokerRevealedRef.current = true;
          }
        }
      }, 3000);
    }

    if (remainingTime > 0) {
      remainingTimeZeroFiredRef.current = false;
    }
  }, [remainingTime, socketData, jokerCard]);

  // ====================== CARD_DEALT (Live Reveal, backlog-safe, no duplicates) ======================
  useEffect(() => {
    if (!socketData || socketData.type !== "CARD_DEALT") return;

    const side = String(socketData.side || "").toUpperCase();
    const backlog = toCardArray(socketData.card);
    const newCard = socketData.newCard;
    if (!newCard) return;

    const eventKey = `${socketData.roundId}-${side}-${newCard}-${backlog.length}`;
    if (processedEventsRef.current.has(eventKey)) return;
    processedEventsRef.current.add(eventKey);

const fullList = [...backlog, newCard].filter(Boolean);
const fullAssets = fullList.map((c) => {
  const asset = mapCardStringToAsset(c);
  if (!asset) console.warn(`[${side}] Asset NOT FOUND for card:`, c);
  return asset;
}).filter(Boolean);

const newAsset = mapCardStringToAsset(newCard);
const dealtRank = extractRank(newCard);
const roundIdAtDealTime = socketData.roundId;

if (side === "JOKER") {
  if (!newAsset) return;
  setTimeout(() => {
    if (String(roundIdAtDealTime) !== prevRoundIdRef.current) return;
    if (jokerRevealedRef.current) return;
    setJokerCard(newAsset);
    setJokerRank(dealtRank);
    jokerRankRef.current = dealtRank;
    jokerRevealedRef.current = true;
  }, 3000);
} else if (side === "ANDAR") {
  if (!newAsset) return;
  setTimeout(async () => {
    if (String(roundIdAtDealTime) !== prevRoundIdRef.current) return;
    await waitForJoker();
    if (String(roundIdAtDealTime) !== prevRoundIdRef.current) return;

    console.log("ANDAR raw cards:", fullList, "→ resolved assets:", fullAssets);

    setRevealedAndar((prev) => {
      const missing = fullAssets.slice(prev.length);
      if (missing.length <= 1) return prev;
      return [...prev, ...missing.slice(0, -1)];
    });

    revealQueueRef.current.push({ side: "ANDAR", asset: newAsset, roundId: roundIdAtDealTime });
    processRevealQueue(isFirstRevealOfRoundRef.current);
    isFirstRevealOfRoundRef.current = false;
  }, 2000);
} else if (side === "BAHAR") {
  if (!newAsset) return;
  setTimeout(async () => {
    if (String(roundIdAtDealTime) !== prevRoundIdRef.current) return;
    await waitForJoker();
    if (String(roundIdAtDealTime) !== prevRoundIdRef.current) return;

    console.log("BAHAR raw cards:", fullList, "→ resolved assets:", fullAssets);

    setRevealedBahar((prev) => {
      const missing = fullAssets.slice(prev.length);
      if (missing.length <= 1) return prev;
      return [...prev, ...missing.slice(0, -1)];
    });

    revealQueueRef.current.push({ side: "BAHAR", asset: newAsset, roundId: roundIdAtDealTime });
    processRevealQueue();
  }, 2000);
}
  }, [socketData]);

  // ====================== Settlement (winner + final cards) ======================
  useEffect(() => {
    if (!socketData || socketData.type !== "TIMER_UPDATE") return;
    const settlement = socketData.settlement;
    if (!settlement || settlement.status !== "SETTLED") return;

    const key = String(settlement.id);
    if (settlementProcessedRef.current.has(key)) return;
    settlementProcessedRef.current.add(key);

    const ra = toCardArray(settlement.andarCards).map(mapCardStringToAsset).filter(Boolean);
    const rb = toCardArray(settlement.baharCards).map(mapCardStringToAsset).filter(Boolean);
    const w = String(settlement.winningSide || "").toUpperCase().includes("ANDAR") ? "ANDAR" : "BAHAR";

    setTimeout(() => {
      if (String(settlement.id) !== String(prevRoundIdRef.current)) return;
      setRevealedAndar((prev) => (ra.length > prev.length ? ra : prev));
      setRevealedBahar((prev) => (rb.length > prev.length ? rb : prev));
      setWinner(w);

      setTimeout(() => {
        if (String(settlement.id) !== String(prevRoundIdRef.current)) return;
        setShowClapping(true);

        if (!animEndFiredRef.current) {
          animEndFiredRef.current = true;
          onAnimEnd?.();
        }

        setTimeout(() => {
          if (String(settlement.id) !== String(prevRoundIdRef.current)) return;
          setWinner(null);
          setShowClapping(false);
        }, 3000);
      }, 500);
    }, 2000);
  }, [socketData]);

  // ====================== Safety timeout for anim end ======================
  useEffect(() => {
    if (!bettingClosed) return;
    if (animEndFiredRef.current) return;

    const safetyTimeout = setTimeout(() => {
      if (!animEndFiredRef.current) {
        animEndFiredRef.current = true;
        onAnimEnd?.();
      }
    }, 8000);

    return () => clearTimeout(safetyTimeout);
  }, [bettingClosed, onAnimEnd]);

  // ====================== Idle/Welcome/Clapping GIF ======================
  const currentGif = showClapping ? clapping : (bettingClosed || jokerCard) ? ideal : welcome;

  const renderCards = (list, label) => {
    if (list.length === 0) return null;
    return list.map((src, i) => (
      <img
        key={`${label}-${i}`}
        src={src}
        alt={`${label}-${i}`}
        className={styles.cardInRow}
        style={{ transition: "all 0.3s ease" }}
      />
    ));
  };

  return (
    <GameAreaLoader showLoader={!hasReceivedData || gameScreenLoader}>
      <div className={styles.gameScreenContainer}>
        <div className={styles.gameMainArea}>
          <div className={styles.gameVideoArea}>
            <div className={styles.gameVideoContainer}>
              <img
                src={backgroundImage}
                alt="Andar Bahar Table"
                className={styles.gameBackgroundImage}
                draggable={false}
              />

              <div className={styles.gameGifOverlay}>
                <img
                  src={currentGif}
                  alt="dealer"
                  className={styles.gameGifImage}
                  draggable={false}
                />
              </div>

              <div className={styles.gameCardsContainer}>
                <div className={styles.playingCardLayout}>
                  <div className={styles.andarRow}>
                    <span className={styles.andarRowLabel}>ANDAR </span>
                    <div className={styles.cardRow}>{renderCards(revealedAndar, "A")}</div>
                  </div>

                  <div className={styles.horizontalDividerPlaying} />

                  <div className={styles.baharRow}>
                    <span className={styles.baharRowLabel}>BAHAR </span>
                    <div className={styles.cardRow}>{renderCards(revealedBahar, "B")}</div>
                  </div>

                  <div className={styles.jokerSectionPlaying}>
                    <span className={styles.jokerLabelPlaying}>JOKER</span>
                    <div className={styles.jokerCardContainer}>
                      <img src={jokerCard || closedCard} alt="Joker" className={styles.jokerCard} />
                    </div>
                  </div>

                  {winner && (
                    <div className={styles.gameWinnerAnimation}>
                      <img src={crown} alt="Winner" className={styles.gameCrownImage} />
                      <span className={styles.gameWinnerText}>Winner: {winner}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        {processingLoader && (
          <div className={styles.dataProcessingOverlay}>
            <div className={styles.cardSpinner} />
          </div>
        )}
      </div>
    </GameAreaLoader>
  );
}